import { useState } from 'react'
import type { Ref } from 'react'
import { useComposedRefs } from '@/src/renderer/lib/patches/compose-refs'
import type { ButtonProps, ButtonVariant } from './Button.types'
import { ShortcutBadge } from './ShortcutBadge'
import { ShortcutHint } from '../ShortcutHint'
import './Button.scss'

/**
 * Button Component
 *
 * Supports two variants (primary/secondary) and flexible keyboard shortcut badges.
 *
 * @example
 * // Primary button with return shortcut
 * <Button shortcut={{ kind: 'return' }}>Continue</Button>
 *
 * @example
 * // Secondary button with ESC shortcut
 * <Button variant="secondary" shortcut={{ kind: 'character', text: 'ESC' }}>Cancel</Button>
 *
 * @example
 * // Button with CMD modifier + key (shows ⌘ S as two separate badges)
 * <Button shortcut={{ kind: 'character', text: 'S', cmd: true }}>Save</Button>
 *
 * @example
 * // Button with Ctrl modifier + key (shows Ctrl/Strg Enter as two separate badges)
 * <Button shortcut={{ kind: 'return', ctrl: true }}>Submit</Button>
 *
 * @example
 * // Array of shortcuts (e.g. multiple keys without modifier)
 * <Button shortcut={[
 *   { kind: 'character', text: 'G' },
 *   { kind: 'character', text: 'H' }
 * ]}>
 *   Go Home
 * </Button>
 *
 * @example
 * // Button with add icon shortcut
 * <Button shortcut={{ kind: 'add' }}>Create New</Button>
 */

/**
 * **Das Kürzel am Outline-Knopf, wenn es ein einzelnes Zeichen ist** (#288): Dort steht ein kleiner `ShortcutHint`
 * (16 × 18 px, wie das `K` auf „Einsortieren“) statt der Taste des `ShortcutBadge` (mindestens 20 × 20 px).
 * Modifier, Wörter, Pfeile und mehrere Kürzel bleiben beim Badge.
 */
function outlineHintText(shortcut: ButtonProps['shortcut'], variant: ButtonVariant): string | null {
  if (variant !== 'outline' || !shortcut || Array.isArray(shortcut)) return null
  const { kind, text, cmd, ctrl, mod, shift } = shortcut
  if (kind !== 'character' || cmd || ctrl || mod || shift) return null
  return text !== undefined && /^[\p{L}\p{N}]$/u.test(text) ? text : null
}

const settledButtons = new WeakSet<HTMLButtonElement>()

/**
 * **Erscheint der Button unter der Maus, steht sein Hover sofort da** (#321). Chromium setzt `:hover` an einem frisch
 * eingesetzten Knoten erst nach dem ersten Stil-Durchlauf; dann lief der 200-ms-Übergang ab, und das neue „Weiter“ im
 * Setup blitzte violett auf (gemessen 12.10.2026). Die ersten zwei Frames trägt der Button deshalb `data-settling`,
 * das SCSS schaltet damit die Übergänge ab. Ein Attribut statt einer Klasse, weil `className` React gehört.
 * Nur einmal je Knoten: Ein neuer `ref` von außen (oder StrictMode) hängt den Ref neu an, ohne dass der Button neu
 * erscheint. Kein Abbruch beim Abhängen — sonst bliebe das Attribut nach StrictModes Doppel-Aufruf für immer stehen.
 */
function settleUnderPointer(node: HTMLButtonElement | null) {
  if (!node || settledButtons.has(node)) return
  settledButtons.add(node)
  node.setAttribute('data-settling', '')
  requestAnimationFrame(() => requestAnimationFrame(() => node.removeAttribute('data-settling')))
}

export function Button({
  onClick,
  children,
  variant = 'primary',
  size = 'md',
  shortcut,
  icon,
  disabled = false,
  type = 'button',
  className,
  ref,
  ...rest
}: ButtonProps & { ref?: Ref<HTMLButtonElement> }) {
  const [isPressed, setIsPressed] = useState(false)
  const buttonRef = useComposedRefs(ref, settleUnderPointer)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.code === 'Enter') && !e.repeat) {
      e.preventDefault()
      setIsPressed(true)
      if (onClick) {
        onClick()
      }
      // Reset nach kurzer Zeit
      setTimeout(() => setIsPressed(false), 200)
    }
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      setIsPressed(false)
    }
  }

  const hintText = outlineHintText(shortcut, variant)

  const buttonClass = `button button--${variant} button--size-${size} ${isPressed ? 'button--pressed' : ''} ${className || ''}`.trim()

  return (
    <button
      {...rest}
      ref={buttonRef}
      type={type}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      className={buttonClass}
      disabled={disabled}
    >
      {icon && <span className="button__icon">{icon}</span>}
      {children && <span className="button__text">{children}</span>}
      {shortcut && (hintText !== null ? (
        <span className="button__shortcut-hint">
          <ShortcutHint variant="emphasized">{hintText}</ShortcutHint>
        </span>
      ) : (
        <div className="button__icon-wrapper">
          <ShortcutBadge shortcut={shortcut} variant={variant} />
        </div>
      ))}
    </button>
  )
}
